import moment from "moment";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getFromServerPublicTopic, getFromServerTopic } from "@utility/slice";
import { IDLE, LOADING, SAVING } from "@src/constants";
import {
  ADD_NAME_AND_DESCRIPTION,
  alertConditionDisplay,
  alertDateConditionDisplay,
  alertDayOfWeekConditionDisplay,
  alertTimeConditionDisplay,
  BAD,
  DAYS_OF_WEEK,
  DIGEST_DAILY,
  GREATER,
  IGNORE,
  LOWER,
  NOTIFY_BOTH,
  NOTIFY_IN_ALARM,
  SELECT_METRIC,
  SPECIFY_CONDITIONS,
} from "./alerts";
import { containsMetric, longName, metricKey, removeMetric } from "./metrics";
import { removeAction as removeActionFromArray } from "./actions";
import { getOperation } from "./mathOptions";
import { formatDate, TODAY_RANGE } from "../helpers/utils";
import { INSTALL_CATEGORIES } from "../iaq/constants";

export const CREATING_ALERT = "creatingAlert";
export const VIEWING_HISTORY = "viewingHistory";
export const TEMPLATES_TAB = "templates";
export const BROWSE_TAB = "browse";
export const GRAPHED_TAB = "graphed";

const SLICE = `alerts`;

const action = (name) => {
  return `${SLICE}/${name}`;
};

const filterList = (searchQuery, list, itemNameFn) => {
  if ("" === searchQuery) {
    return [...list];
  }

  const searchStrings = searchQuery.trim().toLowerCase().split(" ");
  const filteredList = [];

  list.forEach((it) => {
    const matchesNamespace = searchStrings.reduce(
      (previousResult, searchString) =>
        previousResult &&
        it.namespace.toLowerCase().indexOf(searchString) !== -1,
      true,
    );
    if (matchesNamespace) {
      filteredList.push(it);
      return;
    }

    const matchingSubs = [];
    it.namespaces.forEach((sub) => {
      const matchesSubNamespace = searchStrings.reduce(
        (previousResult, searchString) =>
          previousResult &&
          sub.namespace.toLowerCase().indexOf(searchString) !== -1,
        true,
      );
      if (matchesSubNamespace) {
        matchingSubs.push(sub);
        return;
      }

      const matchingMetrics = [];
      sub.items.forEach((m) => {
        const matchingMetric = searchStrings.reduce(
          (previousResult, searchString) =>
            previousResult &&
            itemNameFn(m).toLowerCase().indexOf(searchString) !== -1,
          true,
        );
        if (matchingMetric) {
          matchingMetrics.push(m);
        }
      });
      if (matchingMetrics.length > 0) {
        matchingSubs.push({
          ...sub,
          items: matchingMetrics,
        });
      }
    });
    if (matchingSubs.length > 0) {
      filteredList.push({
        ...it,
        namespaces: matchingSubs,
      });
    }
  });

  return filteredList;
};

export const getFromServer = (action, contextOptionsFn, payloadFn) =>
  getFromServerTopic({ topic: SLICE, action, contextOptionsFn, payloadFn });

export const getFromServerPublic = (action, contextOptionsFn, payloadFn) =>
  getFromServerPublicTopic({
    topic: SLICE,
    action,
    contextOptionsFn,
    payloadFn,
  });

export const initializeAlertsPage = getFromServer("initializeAlertsPage");

export const performMetricSearch = createAsyncThunk(
  action(`performMetricSearch`),
  async (arg, { getState }) => {
    const state = getState().alerts;
    return filterList(
      state.metricSearchQuery,
      state.unfilteredMetricList,
      (item) => {
        return item.values.join("-");
      },
    );
  },
);

export const performTemplateSearch = createAsyncThunk(
  action(`performTemplateSearch`),
  async (action, { getState }) => {
    const state = getState().alerts;
    return filterList(
      state.templateSearchQuery,
      state.unfilteredTemplatesList,
      (item) => {
        return item.name;
      },
    );
  },
);

export const getMetrics = getFromServer("getMetrics");
export const getTemplates = getFromServer("getTemplates");

export const getMetricData = getFromServer(
  "getMetricData",
  async ({ payload }) => payload,
  async (arg, { getState }) => {
    const state = await getState();
    const addEditAlert = state.alerts.addEditAlert;

    if (!addEditAlert) {
      return [];
    }

    const metrics = addEditAlert.metrics.map((it) => {
      const metric = state.alerts.metricsByKey[metricKey(it)];
      return {
        ...it,
        name: longName(metric),
        type: "line",
      };
    });

    const expressions = (
      addEditAlert.expression
        ? [{ alias: "e1", value: addEditAlert.expression, isSelected: true }]
        : [...state.alerts.expressions]
    )
      .filter((it) => it.isSelected)
      .map((it) => {
        return {
          ...it,
          name: `${it.alias} (${it.value})`,
          type: "line",
        };
      });

    return {
      metrics,
      expressions,
    };
  },
);

export const addAlertToBundle = getFromServer(
  "addAlertsToBundle",
  async ({ payload }) => payload,
  async ({ selectedRows, bundle }) => {
    return {
      bundle,
      ids: selectedRows.map((it) => it.id),
    };
  },
);

export const addNote = getFromServer(
  "addNote",
  async ({ arg }) => arg,
  async (arg) => {
    return arg;
  },
);

export const deleteNote = getFromServer(
  "deleteNote",
  async ({ arg }) => arg,
  async (arg) => arg,
);

export const editNote = getFromServer(
  "editNote",
  async ({ arg }) => arg,
  async (arg) => arg,
);

export const copyAlert = getFromServer(
  "copyAlerts",
  async ({ payload }) => payload,
  async (arg) => {
    return arg.map((it) => it.id);
  },
);

export const deleteAlert = getFromServer(
  "deleteAlerts",
  async ({ payload }) => payload,
  async (arg) => {
    return arg.map((it) => it.id);
  },
);

export const disableAlert = getFromServer(
  "disableAlerts",
  async ({ payload }) => payload,
  async (arg) => {
    return arg.map((it) => it.id);
  },
);

export const enableAlert = getFromServer(
  "enableAlerts",
  async ({ payload }) => payload,
  async (arg) => {
    return arg.map((it) => it.id);
  },
);

export const getHistory = getFromServer(
  "getHistory",
  async ({ payload }) => payload,
  async (arg) => {
    const { ids, dates } = arg;
    return {
      ids,
      start: formatDate(dates[0]),
      end: formatDate(dates[1]),
    };
  },
);

export const loadSingleHistory = getFromServerPublic(
  "loadSingleHistory",
  async ({ arg }) => arg,
  async (arg) => arg,
);

export const loadHistoryRecord = getFromServer(
  "loadHistoryRecord",
  async ({ payload }) => {
    return { id: payload.id };
  },
  async (arg) => arg,
);

export const removeAlertFromBundle = getFromServer(
  "removeAlertsFromBundle",
  async ({ payload }) => payload,
  async (arg) => {
    return arg.map((it) => it.id);
  },
);

export const saveAlert = getFromServer(
  "saveAlert",
  async ({ payload }) => payload.id,
  async (payload, { getState }) => {
    const state = getState();
    return {
      ...state.alerts.addEditAlert,
      name: payload.name,
      startDay: moment(state.alerts.addEditAlert.startDay).format("YYYY-MM-DD"),
      endDay: moment(state.alerts.addEditAlert.endDay).format("YYYY-MM-DD"),
      startTime: moment(state.alerts.addEditAlert.startTime).format("HH:mm"),
      endTime: moment(state.alerts.addEditAlert.endTime).format("HH:mm"),
    };
  },
);

const pendingRequest = (state, status) => {
  state.pendingRequests++;
  state.status = status;
};

const completedRequest = (state) => {
  state.pendingRequests--;

  if (state.pendingRequests < 1) {
    state.pendingRequests = 0;
    state.status = IDLE;
  }
};

const reconstructDateTimes = (alerts) => {
  for (const alert of alerts) {
    const startDayParts = alert.startDay.split("-");
    const endDayParts = alert.endDay.split("-");
    alert.startDay = new Date(
      +startDayParts[0],
      +startDayParts[1] - 1,
      +startDayParts[2],
    );
    alert.endDay = new Date(
      +endDayParts[0],
      +endDayParts[1] - 1,
      +endDayParts[2],
    );

    const startTimeParts = alert.startTime.split(":");
    const endTimeParts = alert.endTime.split(":");
    alert.startTime = new Date(
      1970,
      0,
      1,
      +startTimeParts[0],
      +startTimeParts[1],
    );
    alert.endTime = new Date(1970, 0, 1, +endTimeParts[0], +endTimeParts[1]);
  }
  return alerts;
};

const replaceHistoryRow = (state, action) => {
  const newHistoryRow = action.payload.response;
  const { id } = newHistoryRow;
  const idx = state.historyList.findIndex((it) => it.id === id);
  if (idx > -1) {
    state.historyList = [
      ...state.historyList.slice(0, idx),
      newHistoryRow,
      ...state.historyList.slice(idx + 1),
    ];
  }
  state.historyNotesRow = newHistoryRow;
  state.historyDetail[id] = newHistoryRow;
};

const configureCBLTenPercentTemplate = ({
  state,
  entityId,
  entityType,
  metricId,
  name,
}) => {
  const actualToCBLMetricIdMap = new Map([
    [1, 3],
    [4, 8],
    [5, 9],
    [6, 10],
  ]);

  const cblMetricId = actualToCBLMetricIdMap.get(metricId);

  const actualMetric = state.metricsList
    .flatMap((it) => it.namespaces)
    .flatMap((it) => it.items)
    .find(
      (m) =>
        m.metricId === metricId &&
        m.entityId === entityId &&
        m.entityType === entityType,
    );

  const cblMetric = state.metricsList
    .flatMap((it) => it.namespaces)
    .flatMap((it) => it.items)
    .find(
      (m) =>
        m.metricId === cblMetricId &&
        m.entityId === entityId &&
        m.entityType === entityType,
    );

  if (actualMetric && cblMetric) {
    const { addEditAlert: aea } = state;
    const selectedMetrics = [...aea.metrics];
    const metrics = [...state.metrics];

    const metricDefinition = {
      ...actualMetric,
      alias: `m${metrics.length + 1}`,
    };

    if (!containsMetric(selectedMetrics, actualMetric)) {
      selectedMetrics.push(metricDefinition);
    }
    if (!containsMetric(metrics, actualMetric)) {
      metrics.push(metricDefinition);
    }

    const cblMetricDefinition = {
      ...cblMetric,
      alias: `m${metrics.length + 1}`,
    };

    if (!containsMetric(selectedMetrics, cblMetricDefinition)) {
      selectedMetrics.push(cblMetricDefinition);
    }
    if (!containsMetric(metrics, cblMetricDefinition)) {
      metrics.push(cblMetricDefinition);
    }

    state.metrics = metrics;
    state.expressions.push({
      alias: `e${state.expressions.length + 1}`,
      value: `(${metricDefinition.alias} / ${cblMetricDefinition.alias} - 1) * 100`,
      isSelected: true,
    });
    aea.name = name;
    aea.metrics = selectedMetrics;
    aea.expression = state.expressions[0].value;
    aea.threshold = 10;
  }
};

const configureOfflineDetectionTemplate = ({
  state,
  entityId,
  entityType,
  metricId,
  name,
}) => {
  const actualMetric = state.metricsList
    .flatMap((it) => it.namespaces)
    .flatMap((it) => it.items)
    .find(
      (m) =>
        m.metricId === metricId &&
        m.entityId === entityId &&
        m.entityType === entityType,
    );

  const { addEditAlert: aea } = state;
  const selectedMetrics = [...aea.metrics];
  const metrics = [...state.metrics];

  const metricDefinition = {
    ...actualMetric,
    alias: `m${metrics.length + 1}`,
  };

  if (!containsMetric(selectedMetrics, actualMetric)) {
    selectedMetrics.push(metricDefinition);
  }
  if (!containsMetric(metrics, actualMetric)) {
    metrics.push(metricDefinition);
  }

  state.metrics = metrics;
  aea.name = name;
  aea.metrics = selectedMetrics;
  aea.condition = LOWER;
  aea.threshold = -99999;
  aea.datapointsToAlarm = [6, 6];
  aea.missingDataTreatment = BAD;
};

export const slice = createSlice({
  name: "alerts",
  initialState: {
    actionsList: [],
    expressions: [],
    historyAlertChart: undefined,
    historyDateRange: TODAY_RANGE,
    historyDetail: {},
    historyList: undefined,
    historyIds: [],
    historyNotesRow: undefined,
    list: [],
    metrics: [],
    metricsDataList: [],
    metricsList: [],
    metricSelectTab: BROWSE_TAB,
    metricSearchQuery: "",
    pendingRequests: 0,
    searchQuery: "",
    status: LOADING,
    templateSearchQuery: "",
    templatesList: [],
    unfilteredList: [],
    unfilteredMetricList: [],
    unfilteredTemplatesList: [],
    createStep: undefined,
  },
  reducers: {
    addAction: (state, action) => {
      state.addEditAlert = {
        ...state.addEditAlert,
        actions: [
          ...state.addEditAlert.actions,
          { actionId: action.payload.id },
        ],
      };
    },
    addExpression: (state, action) => {
      const mathOption = getOperation(action.payload);

      const value = mathOption
        ? mathOption.createExpression(state.metrics)
        : action.payload;

      state.expressions = [
        ...state.expressions,
        {
          alias: `e${state.expressions.length + 1}`,
          value,
          isSelected: true,
        },
      ];
    },
    changeCondition: (state, action) => {
      state.addEditAlert = {
        ...state.addEditAlert,
        condition: action.payload,
      };
    },
    changeDatapointsToAlarm: (state, action) => {
      state.addEditAlert = {
        ...state.addEditAlert,
        datapointsToAlarm: action.payload,
      };
    },
    changeDaysOfWeek: (state, action) => {
      state.addEditAlert = {
        ...state.addEditAlert,
        daysOfWeek: action.payload,
      };
    },
    changeDigestNotification: (state, action) => {
      state.addEditAlert = {
        ...state.addEditAlert,
        digestNotification: action.payload,
      };
    },
    changeEmailNotifyOn: (state, action) => {
      state.addEditAlert = {
        ...state.addEditAlert,
        emailNotifyOn: action.payload,
      };
    },
    changeEndDay: (state, action) => {
      state.addEditAlert = {
        ...state.addEditAlert,
        endDay: action.payload,
      };
    },
    changeEndTime: (state, action) => {
      state.addEditAlert = {
        ...state.addEditAlert,
        endTime: action.payload,
      };
    },
    changeExpression: (state, action) => {
      state.expressions
        .filter((it) => it.alias === action.payload.alias)
        .forEach((it) => (it.value = action.payload.value));
    },
    changeIncludeHolidays: (state, action) => {
      state.addEditAlert = {
        ...state.addEditAlert,
        includeHolidays: action.payload,
      };
    },
    changeInstallCategories: (state, action) => {
      state.addEditAlert = {
        ...state.addEditAlert,
        installCategories: action.payload,
      };
    },
    changeMetricSelectTab: (state, action) => {
      state.metricSelectTab = action.payload;
    },
    changeMissingDataTreatment: (state, action) => {
      state.addEditAlert = {
        ...state.addEditAlert,
        missingDataTreatment: action.payload,
      };
    },
    changeNoteNotifyOn: (state, action) => {
      state.addEditAlert = {
        ...state.addEditAlert,
        noteNotifyOn: action.payload,
      };
    },
    changeRetriggerHours: (state, action) => {
      state.addEditAlert = {
        ...state.addEditAlert,
        retriggerHours: action.payload,
      };
    },
    changeStartDay: (state, action) => {
      state.addEditAlert = {
        ...state.addEditAlert,
        startDay: action.payload,
      };
    },
    changeStartTime: (state, action) => {
      state.addEditAlert = {
        ...state.addEditAlert,
        startTime: action.payload,
      };
    },
    changeTeamId: (state, action) => {
      state.addEditAlert = {
        ...state.addEditAlert,
        teamId: action.payload,
      };
    },
    changeTextNotifyOn: (state, action) => {
      state.addEditAlert = {
        ...state.addEditAlert,
        textNotifyOn: action.payload,
      };
    },
    changeThreshold: (state, action) => {
      state.addEditAlert = {
        ...state.addEditAlert,
        threshold: action.payload,
      };
    },
    changeWebhookTarget: (state, action) => {
      const { actionId, target } = action.payload;
      const webhook = state.addEditAlert.actions.find(
        (it) => it.actionId === actionId,
      );
      if (webhook) {
        webhook.target = target;
      }
    },
    configureAlertFromTemplate: (state, action) => {
      const { entityId, entityType, metricId, name } = action.payload;

      const templateFn =
        name.indexOf("10% Above CBL") !== -1
          ? configureCBLTenPercentTemplate
          : name.indexOf("Offline") !== -1
            ? configureOfflineDetectionTemplate
            : undefined;

      templateFn({
        state,
        entityId,
        entityType,
        metricId,
        name,
      });

      state.createStep = SPECIFY_CONDITIONS.id;
    },
    createAlert: (state) => {
      state.section = CREATING_ALERT;
      state.createStep = SELECT_METRIC.id;
      state.expressions = [];
      state.metrics = [];
      state.metricsList = state.unfilteredMetricList;
      state.metricSearchQuery = "";
      state.templatesList = state.unfilteredTemplatesList;
      state.templateSearchQuery = "";
      state.addEditAlert = {
        actions: [],
        condition: GREATER,
        datapointsToAlarm: [1, 1],
        daysOfWeek: [...DAYS_OF_WEEK],
        digestNotification: DIGEST_DAILY,
        emailNotifyOn: NOTIFY_BOTH,
        endDay: new Date(1970, 11, 31),
        endTime: new Date(1970, 0, 1, 23, 59),
        includeHolidays: true,
        installCategories: [...INSTALL_CATEGORIES],
        isAlertCreator: true,
        metrics: [],
        missingDataTreatment: IGNORE,
        name: "",
        noteNotifyOn: NOTIFY_IN_ALARM,
        retriggerHours: 0,
        startDay: new Date(1970, 0, 1),
        startTime: new Date(1970, 0, 1, 0, 0),
        teamId: null,
        textNotifyOn: NOTIFY_BOTH,
        threshold: 0,
      };
      state.metricSelectTab = BROWSE_TAB;
    },
    createAlertChangeMetrics: (state, action) => {
      let selectedMetrics = [...state.addEditAlert.metrics];
      let metrics = [...state.metrics];

      for (const metricToRemove of action.payload.unselectedMetrics) {
        selectedMetrics = removeMetric(selectedMetrics, metricToRemove);
        metrics = removeMetric(metrics, metricToRemove);
      }

      for (const metricToAdd of action.payload.selectedMetrics) {
        const metricDefinition = {
          ...metricToAdd,
          alias: `m${metrics.length + 1}`,
        };
        if (!containsMetric(selectedMetrics, metricToAdd)) {
          selectedMetrics.push(metricDefinition);
        }
        if (!containsMetric(metrics, metricToAdd)) {
          metrics.push(metricDefinition);
        }
      }
      state.addEditAlert.metrics = selectedMetrics;
      state.metrics = metrics;
    },
    createAlertNextStep: (state, action) => {
      state.createStep =
        state.createStep === ADD_NAME_AND_DESCRIPTION.id
          ? undefined
          : state.createStep + 1;

      if (action.payload) {
        state.addEditAlert = {
          ...state.addEditAlert,
          ...action.payload,
        };
      }

      if (state.createStep === SPECIFY_CONDITIONS.id) {
        if (
          (state.addEditAlert.threshold ?? null) === null &&
          state.addEditAlert.metrics &&
          state.addEditAlert.metrics.length > 0
        ) {
          const values = state.metricsDataList
            .find(
              (it) =>
                it.metricId === state.addEditAlert.metrics[0].metricId &&
                it.entityId === state.addEditAlert.metrics[0].entityId &&
                it.entityType === state.addEditAlert.metrics[0].entityType,
            )
            .data.map((it) => {
              return it[1];
            });
          const maxValue = Math.max(...values);
          state.addEditAlert.threshold = parseInt(maxValue - 5);
        }
      }
    },
    createAlertPreviousStep: (state) => {
      state.createStep =
        state.createStep === SELECT_METRIC.id
          ? undefined
          : state.createStep - 1;
    },
    editAlert: (state, action) => {
      state.section = CREATING_ALERT;
      state.createStep = SELECT_METRIC.id;
      state.expressions = action.payload.expression
        ? [
            {
              alias: "e1",
              value: action.payload.expression,
              isSelected: true,
            },
          ]
        : [];
      state.metrics = action.payload.metrics;
      state.addEditAlert = {
        ...action.payload,
        expression: undefined,
      };
      state.metricSelectTab = GRAPHED_TAB;
    },
    graphedChangeMetrics: (state, action) => {
      let selectedMetrics = [...state.addEditAlert.metrics];

      const { selectedRowNames, unselectedRowNames } = action.payload;

      unselectedRowNames.forEach((toRemove) => {
        state.expressions
          .filter((it) => it.alias === toRemove && it.isSelected)
          .forEach((it) => {
            it.isSelected = !it.isSelected;
          });

        selectedMetrics = selectedMetrics.filter((it) => {
          return it.alias !== toRemove;
        });
      });

      selectedRowNames.forEach((toAdd) => {
        state.expressions
          .filter((it) => it.alias === toAdd && !it.isSelected)
          .forEach((it) => {
            it.isSelected = !it.isSelected;
          });

        const metricToAdd = state.metrics.find((it) => {
          return it.alias === toAdd;
        });
        if (metricToAdd && !containsMetric(selectedMetrics, metricToAdd)) {
          selectedMetrics.push(metricToAdd);
        }
      });

      state.addEditAlert.metrics = selectedMetrics;
    },
    hideNotes: (state) => {
      state.historyNotesRow = undefined;
    },
    homePage: (state) => {
      state.section = undefined;
      state.status = IDLE;
    },
    performAlertSearch: (state, action) => {
      state.searchQuery = action.payload || "";
      state.list =
        "" === action.payload
          ? [...state.unfilteredList]
          : state.unfilteredList.filter((it) => {
              const searchString = action.payload.toLowerCase();
              const searchFields = [
                it.name,
                it.state,
                ...it.actions.map((action) => {
                  return state.actionsById[action.actionId].name;
                }),
                alertConditionDisplay(it, state.metricsByKey),
                alertDateConditionDisplay(it),
                alertDayOfWeekConditionDisplay(it),
                alertTimeConditionDisplay(it),
              ];
              if (it.bundle) {
                searchFields.push(it.bundle);
              }
              return searchFields.find(
                (f) => f.toLowerCase().indexOf(searchString) !== -1,
              );
            });
    },
    removeAction: (state, action) => {
      const actions = removeActionFromArray(
        state.addEditAlert.actions,
        action.payload,
      );
      state.addEditAlert = {
        ...state.addEditAlert,
        actions,
      };
    },
    updateAlias: (state, action) => {
      const { currentAlias, newAlias } = action.payload;
      const metricsToUpdate = [
        state.metrics.find((it) => it.alias === currentAlias),
        state.addEditAlert.metrics.find((it) => it.alias === currentAlias),
      ];
      metricsToUpdate.forEach((it) => {
        if (it) it.alias = newAlias;
      });

      state.expressions = state.expressions.map((it) => {
        return {
          ...it,
          alias: it.alias === currentAlias ? newAlias : it.alias,
          value: it.value.replaceAll(currentAlias, newAlias),
        };
      });
    },
    updateMetricSearchQuery: (state, action) => {
      state.metricSearchQuery = action.payload || "";
    },
    updateAlertSearchQuery: (state, action) => {
      state.searchQuery = action.payload || "";
    },
    updateTemplateSearchQuery: (state, action) => {
      state.templateSearchQuery = action.payload || "";
    },
    viewHistory: (state, action) => {
      const { alerts, dateRange } = action.payload;

      state.section = VIEWING_HISTORY;
      state.historyIds = alerts ? alerts.map((it) => it.id) : [];
      if (dateRange) {
        state.historyDateRange = dateRange;
      }
      state.historyList = undefined;
      state.status = IDLE;
    },
    viewNotes: (state, action) => {
      state.historyNotesRow = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addAlertToBundle.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(addAlertToBundle.fulfilled, (state, action) => {
        completedRequest(state);
        state.list = reconstructDateTimes(action.payload.response);
        state.addEditAlert = undefined;
      })
      .addCase(addNote.fulfilled, (state, action) => {
        replaceHistoryRow(state, action);
      })
      .addCase(deleteNote.fulfilled, (state, action) => {
        replaceHistoryRow(state, action);
      })
      .addCase(editNote.fulfilled, (state, action) => {
        replaceHistoryRow(state, action);
      })
      .addCase(copyAlert.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(copyAlert.fulfilled, (state, action) => {
        completedRequest(state);
        state.list = reconstructDateTimes(action.payload.response);
        state.addEditAlert = undefined;
      })
      .addCase(deleteAlert.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(deleteAlert.fulfilled, (state, action) => {
        completedRequest(state);
        state.list = reconstructDateTimes(action.payload.response);
        state.addEditAlert = undefined;
      })
      .addCase(disableAlert.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(disableAlert.fulfilled, (state, action) => {
        completedRequest(state);
        state.list = reconstructDateTimes(action.payload.response);
        state.addEditAlert = undefined;
      })
      .addCase(enableAlert.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(enableAlert.fulfilled, (state, action) => {
        completedRequest(state);
        state.list = reconstructDateTimes(action.payload.response);
        state.addEditAlert = undefined;
      })
      .addCase(getMetrics.fulfilled, (state, action) => {
        completedRequest(state);
        state.status = IDLE;

        const { names, metrics } = action.payload.response;
        state.metricsByKey = {};

        metrics
          .flatMap((it) => it.namespaces)
          .flatMap((it) => it.items)
          .forEach((it) => {
            if (it.ei) {
              it.entityId = it.ei;
            }
            if (it.et !== undefined) {
              it.entityType = names[it.et];
            }
            if (it.i) {
              it.isDevice = true;
            }
            if (it.m) {
              it.metricId = it.m;
            }
            if (it.t !== undefined) {
              it.totalizer = !!it.t;
            }
            if (it.u !== undefined) {
              it.unit = names[it.u];
            }
            if (it.v !== undefined) {
              it.values = it.v.map((value) => names[value]);
            }
            state.metricsByKey[metricKey(it)] = it;
          });
        state.metricsList = metrics;
        state.unfilteredMetricList = metrics;
      })
      .addCase(initializeAlertsPage.pending, (state) => {
        pendingRequest(state, LOADING);
        state.section = undefined;
      })
      .addCase(initializeAlertsPage.fulfilled, (state, action) => {
        completedRequest(state);
        state.status = IDLE;

        if (!action.payload?.response?.actions) return;

        const { actions, alerts } = action.payload.response;

        state.actionsList = actions;
        state.actionsById = {};
        state.actionsList.forEach((it) => {
          it.actions.forEach((action) => {
            state.actionsById[action.id] = action;
          });
        });

        state.list = reconstructDateTimes(alerts);
        state.list.forEach((it) => {
          if (it.triggeredFor) {
            it.triggeredFor = it.triggeredFor.sort();
          }
        });
        state.unfilteredList = state.list;
      })
      .addCase(getTemplates.fulfilled, (state, action) => {
        const templatesResponse = action.payload.response;
        const { names, templates: templatesList } = templatesResponse;

        templatesList
          .flatMap((it) => it.namespaces)
          .flatMap((it) => it.items)
          .forEach((it) => {
            if (it.ei) {
              it.entityId = it.ei;
              delete it.ei;
            }
            if (it.et !== undefined) {
              it.entityType = names[it.et];
              delete it.et;
            }
            if (it.i) {
              it.isDevice = true;
              delete it.i;
            }
            if (it.m) {
              it.metricId = it.m;
              delete it.m;
            }
            if (it.n) {
              it.name = it.n;
              delete it.n;
            }
            it.totalizer = !!it.t;
            delete it.t;
          });

        state.templatesList = templatesList;
        state.unfilteredTemplatesList = templatesList;
      })
      .addCase(getMetricData.fulfilled, (state, action) => {
        state.metricsDataList = action.payload.response || [];
      })
      .addCase(getHistory.pending, (state) => {
        state.historyList = undefined;
      })
      .addCase(getHistory.fulfilled, (state, action) => {
        const { meta } = action;
        const { dates } = meta.arg;
        state.historyDateRange = dates;
        state.historyList = action.payload.response;
      })
      .addCase(loadSingleHistory.fulfilled, (state, action) => {
        state.historyAlertChart = {
          ...action.payload.response,
          isPreExpand: true,
        };
        state.historyDetail[state.historyAlertChart.id] =
          state.historyAlertChart;
      })
      .addCase(loadHistoryRecord.fulfilled, (state, action) => {
        const historyRecord = action.payload.response;
        state.historyDetail[historyRecord.id] = historyRecord;
      })
      .addCase(performMetricSearch.pending, (state, action) => {
        state.metricSearchQuery = action.meta.arg || "";
      })
      .addCase(performMetricSearch.fulfilled, (state, action) => {
        state.metricsList = action.payload || [];
      })
      .addCase(performTemplateSearch.pending, (state, action) => {
        state.templateSearchQuery = action.meta.arg || "";
      })
      .addCase(performTemplateSearch.fulfilled, (state, action) => {
        state.templatesList = action.payload || [];
      })
      .addCase(removeAlertFromBundle.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(removeAlertFromBundle.fulfilled, (state, action) => {
        completedRequest(state);
        state.list = reconstructDateTimes(action.payload.response);
        state.addEditAlert = undefined;
      })
      .addCase(saveAlert.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(saveAlert.fulfilled, (state, action) => {
        completedRequest(state);
        state.list = reconstructDateTimes(action.payload.response);
        state.addEditAlert = undefined;
        state.section = undefined;
      });
  },
});

export const {
  addAction,
  configureAlertFromTemplate,
  addExpression,
  changeCondition,
  changeDatapointsToAlarm,
  changeDaysOfWeek,
  changeDigestNotification,
  changeEmailNotifyOn,
  changeEndDay,
  changeEndTime,
  changeExpression,
  changeIncludeHolidays,
  changeInstallCategories,
  changeMetricSelectTab,
  changeMissingDataTreatment,
  changeNoteNotifyOn,
  changeRetriggerHours,
  changeStartDay,
  changeStartTime,
  changeTeamId,
  changeTextNotifyOn,
  changeThreshold,
  changeWebhookTarget,
  createAlert,
  createAlertChangeMetrics,
  createAlertNextStep,
  createAlertPreviousStep,
  editAlert,
  graphedChangeMetrics,
  hideNotes,
  homePage,
  performAlertSearch,
  removeAction,
  updateAlias,
  updateAlertSearchQuery,
  updateMetricSearchQuery,
  updateTemplateSearchQuery,
  viewHistory,
  viewNotes,
} = slice.actions;

export const INITIAL_STATE = slice.getInitialState();

export default slice.reducer;
